import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

import type { SocketMessage } from 'lib/socket/types';
import type { Block } from 'types/api/block';

import useApiQuery, { getResourceKey } from 'lib/api/useApiQuery';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';

const BLOCKS_KEPT = 6;

/** The current time, ticking every second (for "12s ago" and the heartbeat). 0 before mount. */
export function useNow() {
  const [ now, setNow ] = React.useState(0);
  React.useEffect(() => {
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

export function usePrefersReducedMotion() {
  const [ reduced, setReduced ] = React.useState(false);
  React.useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** Counts up to `target` (from the previous value) over `duration` ms; jumps straight there with reduced motion. */
export function useCountUp(target: number | undefined, duration = 1400) {
  const reduced = usePrefersReducedMotion();
  const [ value, setValue ] = React.useState(0);
  const from = React.useRef(0);

  React.useEffect(() => {
    if (target === undefined || Number.isNaN(target)) {
      return;
    }
    if (reduced) {
      from.current = target;
      setValue(target);
      return;
    }
    const start = performance.now();
    const origin = from.current;
    let frame = 0;
    const step = (time: number) => {
      const progress = Math.min(1, (time - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(origin + (target - origin) * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        from.current = target;
      }
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [ target, duration, reduced ]);

  return value;
}

/**
 * Everything the home page shows, live: chain stats, the latest blocks (pushed over the socket as they are
 * sealed) and the latest transactions (refetched when the socket reports new ones).
 */
export function useLiveHome() {
  const queryClient = useQueryClient();

  const stats = useApiQuery('general:stats', { queryOptions: { refetchOnMount: false } });
  const blocks = useApiQuery('general:homepage_blocks');
  const txs = useApiQuery('general:homepage_txs');

  const { refetch: refetchStats } = stats;
  const { refetch: refetchTxs } = txs;

  // The height of the block that arrived last over the socket, so it can make an entrance.
  const [ freshHeight, setFreshHeight ] = React.useState<number>();

  const handleNewBlock: SocketMessage.NewBlock['handler'] = React.useCallback((payload) => {
    queryClient.setQueryData(getResourceKey('general:homepage_blocks'), (prev: Array<Block> | undefined) => {
      const list = prev ? [ ...prev ] : [];
      if (list.some((block) => block.height === payload.block.height)) {
        return list;
      }
      return [ payload.block, ...list ].sort((a, b) => b.height - a.height).slice(0, BLOCKS_KEPT);
    });
    setFreshHeight(payload.block.height);
    refetchStats();
  }, [ queryClient, refetchStats ]);

  const blocksChannel = useSocketChannel({ topic: 'blocks:new_block', isDisabled: blocks.isPending || blocks.isError });
  useSocketMessage({ channel: blocksChannel, event: 'new_block', handler: handleNewBlock });

  const handleNewTxs = React.useCallback(() => {
    refetchTxs();
    refetchStats();
  }, [ refetchTxs, refetchStats ]);

  const txsChannel = useSocketChannel({ topic: 'transactions:new_transaction', isDisabled: txs.isPending || txs.isError });
  useSocketMessage({ channel: txsChannel, event: 'transaction', handler: handleNewTxs });

  return { stats, blocks, txs, freshHeight };
}
